// DashboardWriteService.js

/**
 * DashboardWriteService.js
 *
 * Service for writing dashboard data to Firestore.
 */

// Firebase
import { collection, deleteDoc, doc, getDocs, query, setDoc, Timestamp, updateDoc, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Utilities
import { generateKey } from 'utilities/Keys';
import {
  buildDashboardLayoutPersistencePayload,
  buildDefaultDashboardLayout,
  collectViewIdsFromBlocks,
  removeViewIdFromDashboardLayout,
  resolveDashboardLayoutDocument,
} from './DashboardLayoutModel';

// Constants
const DASHBOARDS_COLLECTION = collections.dashboards;
const DASHBOARD_SCOPE = Object.freeze({
  GLOBAL: 'GLOBAL',
});

const DASHBOARD_SOURCE = Object.freeze({
  APP: 'APP',
  BASE: 'BASE',
});

function normalizeSource(source) {
  const candidate = typeof source === 'string' ? source.trim().toUpperCase() : '';
  if (candidate === DASHBOARD_SOURCE.APP) return DASHBOARD_SOURCE.APP;
  return DASHBOARD_SOURCE.BASE;
}

function normalizeDescription(description) {
  const trimmed = typeof description === 'string' ? description.trim() : '';
  return trimmed || null;
}

/**
 * Creates a new dashboard document.
 *
 * @param {Object} options - Create options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.userKey - Author user key.
 * @param {string} options.title - Dashboard title.
 * @param {string} [options.description] - Optional dashboard description.
 * @param {boolean} [options.isPrivate=false] - Whether dashboard is private.
 * @param {string} [options.source='BASE'] - Dashboard source (`BASE` or `APP`).
 * @param {string|null} [options.appKey=null] - App key when source is `APP`.
 *
 * @returns {Promise<Object|null>} Created dashboard payload.
 */
export async function createDashboard({
  key,
  baseKey,
  userKey,
  title,
  description,
  isPrivate: rawIsPrivate = false,
  source: rawSource,
  appKey: rawAppKey = null,
} = {}) {

  const trimmedTitle = typeof title === 'string' ? title.trim() : '';
  const normalizedDescription = normalizeDescription(description);
  const isPrivate = rawIsPrivate === true;
  const source = normalizeSource(rawSource);
  const appKey = typeof rawAppKey === 'string' && rawAppKey.trim()
    ? rawAppKey.trim()
    : null;

  if (!baseKey || !userKey || !trimmedTitle) return null;
  if (source === DASHBOARD_SOURCE.APP && !appKey) return null;

  const dashboardKey = typeof key === 'string' && key.trim() ? key.trim() : generateKey();
  const now = Timestamp.now();

  const defaultLayout = buildDefaultDashboardLayout();

  const payload = {
    key: dashboardKey,
    baseKey,
    scope: DASHBOARD_SCOPE.GLOBAL,
    source,
    appKey: source === DASHBOARD_SOURCE.APP ? appKey : null,
    title: trimmedTitle,
    titleLower: trimmedTitle.toLowerCase(),
    description: normalizedDescription,
    private: isPrivate,
    blocksByKey: defaultLayout.blocksByKey,
    layout: defaultLayout.layout,
    layoutVersion: defaultLayout.layoutVersion,
    createdBy: userKey,
    createdAt: now,
    updatedAt: now,
    visibility: null,
  };

  await setDoc(doc(db, DASHBOARDS_COLLECTION, dashboardKey), payload, { merge: false });

  return payload;
}

/**
 * Updates an existing dashboard document.
 *
 * @param {string} dashboardKey - Dashboard key.
 * @param {Object} data - Fields to update.
 *
 * @returns {Promise<void>}
 */
export async function updateDashboard(dashboardKey, data = {}) {

  if (!dashboardKey || !data) return;

  const payload = {
    ...data,
    ...(Object.prototype.hasOwnProperty.call(data, 'description') ? { description: normalizeDescription(data.description) } : {}),
    updatedAt: data?.updatedAt || Timestamp.now(),
  };

  if (
    Object.prototype.hasOwnProperty.call(data, 'layout')
    || Object.prototype.hasOwnProperty.call(data, 'blocksByKey')
  ) {
    Object.assign(payload, buildDashboardLayoutPersistencePayload({
      layout: data?.layout,
      blocksByKey: data?.blocksByKey,
    }));
  }

  await updateDoc(doc(db, DASHBOARDS_COLLECTION, dashboardKey), payload);
}

/**
 * Updates dashboard layout fields using the v2 layout model.
 *
 * @param {string} dashboardKey - Dashboard key.
 * @param {object} options - Layout update options.
 * @param {object} options.layout - Layout tree to persist.
 * @param {object} options.blocksByKey - Block map to persist.
 *
 * @returns {Promise<void>}
 */
export async function updateDashboardLayout(dashboardKey, { layout, blocksByKey } = {}) {
  if (!dashboardKey) return;

  await updateDashboard(dashboardKey, buildDashboardLayoutPersistencePayload({
    layout,
    blocksByKey,
  }));
}

/**
 * Deletes a dashboard by key.
 *
 * @param {string} dashboardKey - Dashboard key.
 *
 * @returns {Promise<void>}
 */
export async function deleteDashboardByKey(dashboardKey) {

  if (!dashboardKey) return;

  await deleteDoc(doc(db, DASHBOARDS_COLLECTION, dashboardKey));
}

/**
 * Removes a deleted view reference from dashboard layout blocks.
 *
 * This function is idempotent: running it multiple times is safe.
 *
 * @param {Object} options - Cleanup options.
 * @param {string} options.viewId - Deleted view key.
 * @param {string} [options.baseKey] - Optional base key for narrower query.
 *
 * @returns {Promise<number>} Number of dashboards updated.
 */
export async function removeViewFromDashboards({ viewId, baseKey } = {}) {

  const cleanedViewId = typeof viewId === 'string' ? viewId.trim() : '';
  const cleanedBaseKey = typeof baseKey === 'string' ? baseKey.trim() : '';

  if (!cleanedViewId) return 0;

  const dashboardsQuery = cleanedBaseKey
    ? query(collection(db, DASHBOARDS_COLLECTION), where('baseKey', '==', cleanedBaseKey))
    : query(collection(db, DASHBOARDS_COLLECTION));

  const snapshot = await getDocs(dashboardsQuery);
  if (!snapshot?.docs?.length) return 0;

  const updates = [];

  snapshot.docs.forEach((docSnap) => {
    const data = docSnap.data() || {};
    const resolved = resolveDashboardLayoutDocument(data);
    const viewIds = collectViewIdsFromBlocks(resolved.blocksByKey);
    if (!viewIds.includes(cleanedViewId)) return;

    const cleanedLayout = removeViewIdFromDashboardLayout({
      blocksByKey: resolved.blocksByKey,
      layout: resolved.layout,
      viewId: cleanedViewId,
    });

    updates.push(
      updateDoc(doc(db, DASHBOARDS_COLLECTION, docSnap.id), {
        ...buildDashboardLayoutPersistencePayload({
          blocksByKey: cleanedLayout.blocksByKey,
          layout: cleanedLayout.layout,
        }),
        updatedAt: Timestamp.now(),
      })
    );
  });

  if (!updates.length) return 0;

  await Promise.all(updates);
  return updates.length;
}
